import { updateDocument, fetchDocument } from '../firebase-utils';
import {
  AdminsCollection,
  UsersCollection,
  LocationsCollection,
} from '../collections';
import { SyntraxisUser, FetchResult, UpdateResult } from '../types/types';

export interface UserSettings {
  reviewReplyGuidelines?: string;
  autoReplyEnabled?: boolean;
  // Message shown on the public review landing page before redirecting to Google reviews
  reviewLandingMessage?: string;
  checkinsWelcomeSubject?: string;
  checkinsWelcomeMessage?: string;
  // Branding + offer details for check-in welcome email
  logoUrl?: string;
  // Custom email template content
  emailTemplateHtml?: string;
  checkinsOfferPercent?: number;
  checkinsOfferValidity?: number;
  checkinsOfferCode?: string;
  checkinsOfferDetails?: string;
}

export const getUserSettings = async (
  userId: string,
): Promise<FetchResult<SyntraxisUser>> => {
  return fetchDocument<SyntraxisUser>(UsersCollection, userId);
};

export const updateUserSettings = async (
  userId: string,
  settings: UserSettings,
): Promise<UpdateResult> => {
  try {
    // Determine where to save: admin doc takes precedence, else user doc; if neither exists yet, write to both
    const targets: string[] = [];
    const adminSnap = await fetchDocument<SyntraxisUser>(
      AdminsCollection,
      userId,
    );
    if (adminSnap.success && adminSnap.data) {
      targets.push(`${AdminsCollection}/${userId}`);
    }

    const userSnap = await fetchDocument<SyntraxisUser>(
      UsersCollection,
      userId,
    );
    if (userSnap.success && userSnap.data) {
      targets.push(`${UsersCollection}/${userId}`);
    }

    if (targets.length === 0) {
      targets.push(
        `${AdminsCollection}/${userId}`,
        `${UsersCollection}/${userId}`,
      );
    }

    for (const docPath of targets) {
      const result = await updateDocument<UserSettings>({ settings }, docPath);
      if (!result.success) {
        return result;
      }
    }
    return { success: true, message: 'Settings updated' };
  } catch (e) {
    return { success: false, message: 'Failed to update settings' };
  }
};

// New: location-scoped settings
export type LocationSettings = UserSettings;

export const getLocationSettings = async (
  locationId: string,
): Promise<FetchResult<{ settings?: LocationSettings }>> => {
  return fetchDocument<{ settings?: LocationSettings }>(
    LocationsCollection,
    locationId,
  );
};

export const updateLocationSettings = async (
  locationId: string,
  settings: LocationSettings,
): Promise<UpdateResult> => {
  return updateDocument<LocationSettings>(
    { settings },
    `${LocationsCollection}/${locationId}`,
  );
};
